import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Trash2, Users, Info, Shield, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { teamApi } from '@/services/endpoints/teamApi'
import { toast } from 'react-hot-toast'
import { showErrorToast } from '@/utils/errorToast'
import type { Team } from '@/types'
import type { TeamInvitationData } from '@/services/endpoints/teamApi'

const teamMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  teamId: z.string().min(1, 'Team selection is required'),
})

const teamMembersSchema = z.object({
  members: z.array(teamMemberSchema).min(0),
})

type TeamMemberData = z.infer<typeof teamMemberSchema>
type TeamMembersFormData = z.infer<typeof teamMembersSchema>

interface TeamMembersOnboardingFormProps {
  onSubmit: (members: TeamMemberData[]) => Promise<void>
  onSkip: () => void
  loading?: boolean
  className?: string
}

export const TeamMembersOnboardingForm: React.FC<TeamMembersOnboardingFormProps> = ({
  onSubmit,
  onSkip,
  loading = false,
  className,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [teams, setTeams] = useState<Team[]>([])
  const [loadingTeams, setLoadingTeams] = useState(true)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [invitedCount, setInvitedCount] = useState(0)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<TeamMembersFormData>({
    resolver: zodResolver(teamMembersSchema),
    defaultValues: {
      members: [{ firstName: '', lastName: '', email: '', teamId: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'members',
  })

  const watchedMembers = watch('members')

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoadingTeams(true)
        const response = await teamApi.getTeams({ page: 1, limit: 10, status: 'active' })
        
        // The actual response structure from the API
        const responseData = response.data as any // Type assertion due to API response mismatch
        
        if (responseData?.data?.teams) {
          setTeams(responseData.data.teams)
          // If there's only one team, auto-select it for all members
          if (responseData.data.teams.length === 1) {
            fields.forEach((_, index) => {
              setValue(`members.${index}.teamId`, responseData.data.teams[0].id)
            })
          }
        }
      } catch (error) {
        console.error('Failed to fetch teams:', error)
      } finally {
        setLoadingTeams(false)
      }
    }

    fetchTeams()
  }, [])

  const handleFormSubmit = async (data: TeamMembersFormData) => {
    try {
      setIsSubmitting(true)
      // Filter out empty members and group by team
      const validMembers = data.members.filter(
        member => member.firstName.trim() && member.lastName.trim() && member.email.trim() && member.teamId
      )

      // Group members by team ID
      const membersByTeam = validMembers.reduce((acc, member) => {
        if (!acc[member.teamId]) {
          acc[member.teamId] = []
        }
        acc[member.teamId].push({
          email: member.email,
          role: 'Admin', // Default role for onboarding
          firstName: member.firstName,
          lastName: member.lastName
        })
        return acc
      }, {} as Record<string, TeamInvitationData[]>)

      // Send invitations for each team
      const invitationPromises = Object.entries(membersByTeam).map(([teamId, invitations]) => 
        teamApi.sendTeamInvitations(teamId, { invitations })
      )

      const responses = await Promise.all(invitationPromises)
      
      // Process responses to count successful and failed invitations
      let totalSuccessful = 0
      let totalFailed = 0
      const failedInvitations: Array<{ email: string; reason: string }> = []
      
      responses.forEach(response => {
        const responseData = response.data as any
        if (responseData?.data?.invitations) {
          const { successful, failed } = responseData.data.invitations
          
          if (successful && successful.length > 0) {
            totalSuccessful += successful.length
          }
          
          if (failed && failed.length > 0) {
            totalFailed += failed.length
            failedInvitations.push(...failed)
          }
        }
      })
      
      // Show appropriate toast messages
      if (failedInvitations.length > 0) {
        // Check if all failures are due to domain restrictions
        const domainErrors = failedInvitations.filter(inv => 
          inv.reason.includes('Email domain must match business domain')
        )
        
        if (domainErrors.length === failedInvitations.length) {
          // All failures are domain related
          const domainMatch = domainErrors[0].reason.match(/@[\w.-]+/)
          const requiredDomain = domainMatch ? domainMatch[0] : 'business domain'
          
          toast.error(
            `All invitations failed: Email domain restriction\n\nTeam members must use ${requiredDomain} email addresses. Please use company email addresses.`,
            { duration: 8000 }
          )
        } else {
          // Mixed failures
          const failureDetails = failedInvitations.map(inv => `${inv.email}: ${inv.reason}`).join('\n')
          toast.error(
            `${totalFailed} invitation(s) failed\n\n${failureDetails}`,
            { duration: 8000 }
          )
        }
        
        // If some succeeded, show that too
        if (totalSuccessful > 0) {
          toast.success(
            `${totalSuccessful} invitation(s) sent successfully`,
            { duration: 4000 }
          )
        }
      } else if (totalSuccessful > 0) {
        // All successful
        toast.success(
          `All ${totalSuccessful} invitation(s) sent successfully!\n\nTeam members will receive email invitations to join.`,
          { duration: 4000 }
        )
      }
      
      // Show success modal with the count of successful invitations
      setInvitedCount(totalSuccessful)
      setShowSuccessModal(true)
    } catch (error: unknown) {
      console.error('Failed to submit team members:', error)

      // Handle API error
      const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message || 'Failed to send invitations'
      showErrorToast(error, `Failed to send invitations\n\n${errorMessage}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const addMember = () => {
    append({ firstName: '', lastName: '', email: '', teamId: teams.length === 1 ? teams[0].id : '' })
  }

  const removeMember = (index: number) => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  const hasValidMembers = watchedMembers.some(
    member => member.firstName.trim() || member.lastName.trim() || member.email.trim()
  )

  const handleInviteMore = () => {
    setShowSuccessModal(false)
    // Reset form to initial state with one empty member
    // Remove all fields except the first one (in reverse order to avoid index issues)
    for (let i = fields.length - 1; i > 0; i--) {
      remove(i)
    }
    setValue('members.0.firstName', '')
    setValue('members.0.lastName', '')
    setValue('members.0.email', '')
    setValue('members.0.teamId', teams.length === 1 ? teams[0].id : '')
  }

  const handleContinueToDashboard = async () => {
    setShowSuccessModal(false)
    await onSubmit([]) // Call onSubmit with empty array to proceed
  }

  return (
    <Card className={cn("w-full max-w-4xl mx-auto", className)}>
      <CardHeader className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4 mx-auto">
          <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
        </div>
        <CardTitle className="text-2xl font-bold text-foreground">
          Add Team Members
        </CardTitle>
        <CardDescription className="text-muted-foreground max-w-2xl mx-auto">
          Invite your team members to collaborate. You can always add more members later from the Teams page.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Admin Role Disclaimer */}
        <Alert className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/30">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>
                All team members will be assigned the <strong>"Admin"</strong> role by default. 
                You can modify roles and permissions later from the Teams page.
              </span>
            </div>
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Team Members
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMember}
                disabled={isSubmitting || loading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </div>

            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 border border-border rounded-lg bg-muted/50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-foreground">
                      Member {index + 1}
                    </h4>
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(index)}
                        disabled={isSubmitting || loading}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="space-y-2">
                      <Label htmlFor={`members.${index}.firstName`}>
                        First Name
                      </Label>
                      <Input
                        id={`members.${index}.firstName`}
                        placeholder="John"
                        {...register(`members.${index}.firstName`)}
                        error={errors.members?.[index]?.firstName?.message}
                        disabled={isSubmitting || loading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`members.${index}.lastName`}>
                        Last Name
                      </Label>
                      <Input
                        id={`members.${index}.lastName`}
                        placeholder="Doe"
                        {...register(`members.${index}.lastName`)}
                        error={errors.members?.[index]?.lastName?.message}
                        disabled={isSubmitting || loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`members.${index}.email`}>
                        Email Address
                      </Label>
                      <Input
                        id={`members.${index}.email`}
                        type="email"
                        placeholder="john.doe@company.com"
                        {...register(`members.${index}.email`)}
                        error={errors.members?.[index]?.email?.message}
                        disabled={isSubmitting || loading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`members.${index}.teamId`}>
                        Team
                      </Label>
                      <Select
                        value={watchedMembers[index]?.teamId || ''}
                        onValueChange={(value) => setValue(`members.${index}.teamId`, value)}
                        disabled={isSubmitting || loading || loadingTeams}
                      >
                        <SelectTrigger 
                          id={`members.${index}.teamId`}
                          className={errors.members?.[index]?.teamId ? 'border-red-500' : ''}
                        >
                          <SelectValue placeholder={loadingTeams ? "Loading teams..." : "Select a team"} />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No teams available
                            </div>
                          ) : (
                            teams.map((team) => (
                              <SelectItem key={team.id} value={team.id}>
                                {team.name || `Team ${team.id}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {errors.members?.[index]?.teamId && (
                        <p className="text-sm text-red-500 mt-1">
                          {errors.members[index]?.teamId?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="ghost"
              onClick={onSkip}
              disabled={isSubmitting || loading}
              className="text-muted-foreground hover:text-foreground"
            >
              Skip this step
            </Button>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={isSubmitting || loading || !hasValidMembers}
                className="min-w-[120px]"
              >
                {isSubmitting || loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Adding...
                  </>
                ) : (
                  'Add Team Members'
                )}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <DialogTitle className="text-center">Team Members Invited Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              {invitedCount === 1 
                ? `1 team member has been invited and will receive an email to join your workspace.`
                : `${invitedCount} team members have been invited and will receive emails to join your workspace.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleInviteMore}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Invite More Members
            </Button>
            <Button
              type="button"
              onClick={handleContinueToDashboard}
              className="w-full sm:w-auto"
            >
              Continue to Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}